import { useMemo } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import { BasePieChart } from "../../../components/graphs/PieChart"
import type { FeedbackCountInsight } from "../../../types/generated/api/FeedbackCountInsight"
import type { LabelInsight } from "../../../types/generated/api/LabelInsight"
import { RealTimeInsightsAPI } from "../Api"
import { generateLabelInsightChartData, generateLabelInsightsSummaryChartData } from "../helpers"

/**
 * MetaData Component
 * Displays real-time insights about project data including:
 * - Total, successful, and failed data points
 * - Decision distribution visualization
 * - Feedback distribution visualization
 * @returns React.ReactElement
 */
export const LabelFeedbackInsights = (): React.ReactElement => {
  const { id: projectId } = useParams<{ id: string }>()

  const [searchParams, _] = useSearchParams()

  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  const { t } = useTranslation("insights", {
    keyPrefix: "LabelFeedbackInsights",
  })

  const {
    isLoading: isLoadingLabelInsight,
    data: labelInsight,
    isError: isLabelsInsightError,
  } = useQuery<AxiosResponse<LabelInsight>, AxiosError>({
    queryKey: ["real_time_insights", "label_insight", projectId, startDate, endDate],
    queryFn: () =>
      RealTimeInsightsAPI.fetchLabelInsight({
        project_uuid: projectId as string,
        start_time: startDate as string,
        end_time: endDate as string,
      }),
    enabled: !!projectId && !!startDate && !!endDate,
  })

  const {
    isLoading: isLoadingFeedbackInsight,
    data: feedbackInsight,
    isError: isFeedbackInsightError,
  } = useQuery<AxiosResponse<FeedbackCountInsight>, AxiosError>({
    queryKey: ["real_time_insights", "feedback_counts", projectId, startDate, endDate],
    queryFn: () =>
      RealTimeInsightsAPI.fetchFeedbackCount({
        project_uuid: projectId as string,
        start_time: startDate as string,
        end_time: endDate as string,
      }),
    enabled: !!projectId && !!startDate && !!endDate,
  })

  // Process the fetched data to create chart data for the pie chart visualization
  const chartData = useMemo(
    () =>
      generateLabelInsightChartData({
        data: labelInsight?.data,
        isLoading: isLoadingLabelInsight,
      }),
    [isLoadingLabelInsight, labelInsight?.data],
  )

  // Process the fetched data to create summary chart data with success/failure breakdown
  const SummaryChartData = useMemo(
    () =>
      generateLabelInsightsSummaryChartData({
        data: labelInsight?.data,
        isLoading: isLoadingLabelInsight,
      }),
    [isLoadingLabelInsight, labelInsight?.data],
  )

  // Filter out "NA" feedback entries
  const filteredFeedbackData = feedbackInsight?.data.data?.filter((item) => item.feedback !== "NA")
  // Calculate new total feedback count
  const feedbackTotal = filteredFeedbackData?.reduce((sum, item) => sum + item.count, 0) ?? 0

  const feedbackData = filteredFeedbackData?.map((item) => ({
    name: item.feedback,
    value: item.count,
    percent: (item.count / (feedbackTotal || 1)) * 100,
  }))

  // Render the BasePieChart component
  return (
    <Grid container item xs={12} spacing={2}>
      <Grid item xs={12} md={4}>
        <InfoBlock
          type="large"
          title={t("infoBlocks.totalDataPoints.title")}
          text={labelInsight?.data?.count ?? 0}
          isLoading={isLoadingLabelInsight}
          titleTooltip={t("infoBlocks.totalDataPoints.tooltip")}
          textTransform="initial"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <InfoBlock
          type="large"
          title={t("infoBlocks.successfulDataPoints.title")}
          text={SummaryChartData["Success"]?.value ?? 0}
          isLoading={isLoadingLabelInsight}
          titleTooltip={t("infoBlocks.successfulDataPoints.tooltip")}
          textTransform="initial"
          tagProps={
            labelInsight?.data?.count
              ? {
                  text: `${(((SummaryChartData["Success"]?.value ?? 0) / (labelInsight?.data?.count ?? 0)) * 100).toFixed(2)}%`,
                  variant: "green",
                }
              : undefined
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <InfoBlock
          title={t("infoBlocks.failedDataPoints.title")}
          text={SummaryChartData["Failed"]?.value ?? 0}
          isLoading={isLoadingLabelInsight}
          titleTooltip={t("infoBlocks.failedDataPoints.tooltip")}
          textTransform="initial"
          type="large"
          tagProps={
            labelInsight?.data?.count
              ? {
                  text: `${(((SummaryChartData["Failed"]?.value ?? 0) / (labelInsight?.data?.count ?? 0)) * 100).toFixed(2)}%`,
                  variant: "yellow",
                }
              : undefined
          }
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <BasePieChart
          title={t("decisionsBreakdown.title")}
          tooltipText={<Trans>{t("decisionsBreakdown.tooltip")}</Trans>}
          isLoading={isLoadingLabelInsight}
          data={chartData}
          graphHeight={150}
          label={{
            name: t("decisionsBreakdown.total"),
            value: labelInsight?.data.count ?? 0,
          }}
          orientation="vertical"
          emptyState={
            isLabelsInsightError
              ? {
                  title: t("decisionsBreakdown.emptyState.insightFailed"),
                }
              : chartData.length === 0
                ? {
                    title: t("decisionsBreakdown.emptyState.noData"),
                  }
                : undefined
          }
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6}>
        <BasePieChart
          title={t("feedbackBreakdown.title")}
          tooltipText={<Trans>{t("feedbackBreakdown.tooltip")}</Trans>}
          isLoading={isLoadingFeedbackInsight}
          data={feedbackData ?? []}
          graphHeight={150}
          label={{
            name: t("feedbackBreakdown.total"),
            value: feedbackTotal,
          }}
          orientation="vertical"
          emptyState={
            isFeedbackInsightError
              ? {
                  title: t("feedbackBreakdown.emptyState.insightFailed"),
                }
              : feedbackData?.length === 0
                ? {
                    title: t("feedbackBreakdown.emptyState.noData"),
                  }
                : undefined
          }
        />
      </Grid>
    </Grid>
  )
}
